import { action, observable } from 'mobx'

import { trackPendingMapArt } from '~/client/src/shared/map/mapArtPending'
import Markup from '~/client/src/shared/models/Markup'

import {
  ITextMarkupSpec,
  specOfMarkup,
  textMarkupFont,
} from './markupTextStyle'
import { ITextLayout, layoutTextMarkups } from './textMarkupLayout'

interface IEntry {
  id: string
  spec: ITextMarkupSpec
  layout: ITextLayout
}

// Content, not map state: shared by every mount and every map (the export map too).
const byKey = new Map<string, IEntry>()
const byId = new Map<string, IEntry>()
let nextId = 1

const keyOf = (spec: ITextMarkupSpec): string =>
  JSON.stringify([
    spec.text,
    spec.fontSize,
    spec.bold,
    spec.italic,
    spec.underline,
    spec.color,
    spec.fillColor,
    spec.textAlign,
    spec.boxWidth,
  ])

// Ids are assigned per distinct spec, not hashed, so two specs can never share one.
// New specs are measured here, before render, in one batched pass.
export function registerTextMarkupSpecs(specs: ITextMarkupSpec[]): string[] {
  const fresh = new Map<string, ITextMarkupSpec>()
  specs.forEach(spec => {
    const key = keyOf(spec)
    if (!byKey.has(key)) fresh.set(key, spec)
  })
  if (fresh.size) {
    const list = [...fresh.values()]
    layoutTextMarkups(list).forEach((layout, i) => {
      const entry = { id: `mkt-${nextId++}`, spec: list[i], layout }
      byKey.set(keyOf(list[i]), entry)
      byId.set(entry.id, entry)
    })
  }
  return specs.map(spec => byKey.get(keyOf(spec)).id)
}

export function textMarkupEntry(id: string): IEntry | undefined {
  return byId.get(id)
}

// The layouts of the text markups that can be measured now (text, faces settled), by markup
// id. Reading the face table lets a derivation run again when faces settle.
export function textMarkupLayouts(markups: Markup[]): Map<string, ITextLayout> {
  const texts = markups
    .filter(markup => markup.kind === 'text' && markup.coordinates?.length)
    .map(markup => ({ id: markup.id, spec: specOfMarkup(markup) }))
    .filter(({ spec }) => spec.text !== '' && areTextMarkupFacesSettled(spec))
  const ids = registerTextMarkupSpecs(texts.map(text => text.spec))
  return new Map(texts.map((text, i) => [text.id, byId.get(ids[i]).layout]))
}

export function mapRatio(map: mapboxgl.Map): number {
  const canvas = map.getCanvas()
  return canvas.clientWidth
    ? canvas.width / canvas.clientWidth
    : window.devicePixelRatio
}

const FACE_TIMEOUT_MS = 3000
// Faces given up on or loaded since we asked; the browser's own check covers the rest.
const settledFaces = observable.set<string>()
const loadingFaces = new Set<string>()

const faceKey = (spec: ITextMarkupSpec): string =>
  `${textMarkupFont(spec)}|${spec.text}`

// Settled = loaded, failed or timed out. Measuring or painting before then would bake
// fallback metrics into a raster that never updates.
export function areTextMarkupFacesSettled(spec: ITextMarkupSpec): boolean {
  return (
    settledFaces.has(faceKey(spec)) ||
    document.fonts.check(textMarkupFont(spec), spec.text || 'x')
  )
}

export function loadTextMarkupFaces(specs: ITextMarkupSpec[]): void {
  specs.forEach(spec => {
    const key = faceKey(spec)
    if (loadingFaces.has(key) || areTextMarkupFacesSettled(spec)) return
    loadingFaces.add(key)
    const settling = Promise.race([
      document.fonts
        .load(textMarkupFont(spec), spec.text || 'x')
        .catch(() => []),
      new Promise(resolve => setTimeout(resolve, FACE_TIMEOUT_MS)),
    ]).then(
      action(() => {
        loadingFaces.delete(key)
        settledFaces.add(key)
      }),
    )
    // Settling re-renders the mount with the text before a capture's next frame.
    trackPendingMapArt(settling)
  })
}
